/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-floating-promises */
import { addDays, differenceInDays } from "date-fns";
import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { MultiSelect, Select, Loader } from "@mantine/core";
import { getCompanyEventList, getCompanyTrends } from "../../../../services/companyEndpoint";
import DateRangeDropdown from "../../../../shared/ui/dropdown/DateRangeDropdown";
import classNames from "../../../../utils/classNames";
import dateConverter, { dateFormatter } from "../../../../utils/dateConverter";
import TrendsChart from "./components/TrendsChart";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import { useTraitsAndMetrics } from "../../../../hooks/QueryHooks";

const AccountTrends = () => {
  const workspaceId = useWorkspaceStore((state) => state.workspace?.id);
  const [events, setEvents] = useState<any>([]);
  const [dateRange, setDateRange] = useState<any>({
    selection: {
      startDate: addDays(new Date(), -6),
      endDate: new Date(),
      key: "selection",
    },
  });
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [data, setData] = useState<any>([]);
  const params: any = useParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const hasInitializedRef = useRef<boolean>(false);
  
  // Default events to use as fallback
  const defaultEvents = [
    { value: 'page_view', label: 'Page View' },
    { value: 'login', label: 'Login' },
    { value: 'signup', label: 'Signup' },
    { value: 'purchase', label: 'Purchase' }
  ];

  // Define the type for the traits data
  type TraitsData = {
    accountMetrics?: {
      list?: Array<{
        name: string;
        display_name: string;
      }>;
    };
  };
  
  // Use the traits and metrics data from the API with proper typing
  const { data: rawTraitsData } = useTraitsAndMetrics({ workspaceId: workspaceId || '' });
  const traitsData = rawTraitsData as TraitsData;
  
  useEffect(() => {
    // Only proceed if we have the required parameters
    if (!params.accountId || !workspaceId) {
      return;
    }
    
    // Prevent duplicate initializations when component re-renders
    if (hasInitializedRef.current && events.length > 0) {
      return;
    }
    
    const fetchEvents = async () => {
      setIsLoading(true);
      try {
        // First check if we have metrics data from the workspace traits API
        if (traitsData?.accountMetrics?.list && traitsData.accountMetrics.list.length > 0) {
          // Using metrics from workspace traits API
          const metricOptions = traitsData.accountMetrics.list
            .filter((metric: any) => !metric.name.startsWith('default_metric_'))
            .map((metric: any) => ({
              value: metric.name.replace('metric_', ''),
              label: metric.display_name || metric.name
            }));
          
          if (metricOptions.length > 0) {
            setEvents(metricOptions);
            // Only set selected events if none are currently selected
            if (!selectedEvents.length) {
              setSelectedEvents([metricOptions[0].value]);
            }
            hasInitializedRef.current = true;
            setIsLoading(false);
            return;
          }
        }
        
        // Fallback to fetching events from the company events API
        const currentData = await getCompanyEventList({
          external_id: params.accountId,
          workspace_identifier: workspaceId,
        });
        
        if (currentData.data && Array.isArray(currentData.data.results) && currentData.data.results.length > 0) {
          const eventOptions = currentData.data.results
            .map((event: any) => ({
              value: event.event_name,
              label: event.event_name.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
            }))
            .filter((event: any) => event.value);
          
          if (eventOptions.length > 0) {
            setEvents(eventOptions);
            // Only set selected events if none are currently selected
            if (!selectedEvents.length) {
              setSelectedEvents([eventOptions[0].value]);
            }
            hasInitializedRef.current = true;
            setIsLoading(false);
            return;
          }
        }
        
        // If we reach here, use default events
        setEvents(defaultEvents);
        if (!selectedEvents.length) {
          setSelectedEvents(['page_view']);
        }
        hasInitializedRef.current = true;
      } catch (error) {
        console.error('Error fetching events:', error);
        // Use default events as fallback
        setEvents(defaultEvents);
        if (!selectedEvents.length) {
          setSelectedEvents(['page_view']);
        }
      } finally {
        setIsLoading(false);
        hasInitializedRef.current = true;
      }
    };
    
    fetchEvents();
  }, [params.accountId, workspaceId, traitsData?.accountMetrics?.list]);

  // Effect for fetching trend data when selectedEvents changes
  useEffect(() => {
    if (!selectedEvents?.length || !selectedEvents[0] || !params.accountId || !workspaceId) {
      return;
    }
    
    const getTable = async () => {
      setIsLoading(true);
      let bucketSize: any;
      const difference = differenceInDays(
        dateRange.selection.endDate,
        dateRange.selection.startDate,
      );
      if (difference < 14) {
        bucketSize = "day";
      } else if (difference < 63) {
        bucketSize = "week";
      } else if (difference < 365) {
        bucketSize = "month";
      } else {
        bucketSize = "year";
      }
      try {
        const graphData = await getCompanyTrends({
          workspace_identifier: workspaceId,
          external_id: params.accountId,
          event_names: selectedEvents,
          bucket_size: bucketSize,
          from_date: dateConverter(dateRange.selection.startDate),
          to_date: dateConverter(dateRange.selection.endDate),
        });
        
        if (graphData.data && graphData.data.results) {
          if (Array.isArray(graphData.data.results) && 
              Array.isArray(graphData.data.results[0])) {
            const dataPoints = graphData.data.results[0];
            const formattedData = dataPoints.map((item: any) => ({
              ...item,
              date: dateFormatter(item.date),
            }));
            setData(formattedData);
          } else {
            console.error('Unexpected data structure in API response:', graphData.data.results);
            setData([]);
          }
        } else {
          console.error('Invalid API response structure:', graphData);
          setData([]);
        }
      } catch (e) {
        console.error('Error fetching trend data:', e);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    getTable();
  }, [params.accountId, selectedEvents, dateRange, workspaceId]);

  return (
    <div className="h-full flex flex-col gap-3">
      <div className="ml-auto px-4 flex gap-2">
        <div className="flex flex-col">
          <h4 className="text-gray-500 text-xs">Select events</h4>
          <MultiSelect
          classNames={{
            input: 'bg-white !hover:bg-white',
          }}
            value={selectedEvents}
            searchable
            onChange={setSelectedEvents}
            data={events}
            disabled={isLoading || events.length === 0}
          />
        </div>
        <DateRangeDropdown
          state={dateRange}
          setState={setDateRange}
        />
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <Loader size="lg" />
        </div>
      ) : selectedEvents?.length && selectedEvents[0] ? (
        <TrendsChart data={data} />
      ) : (
        <h4 className="text-lg text-gray-500 w-[80%] self-center h-full text-center my-10 flex items-center justify-center font-bold italic rounded-md bg-gray-50">
          {events.length > 0 
            ? "Please select an event from the dropdown list to begin looking at trends"
            : "No events available for this account. Please check back later."}
        </h4>
      )}
    </div>
  );
};

export default AccountTrends;
